import { DefaultService, MintCard, Order } from "@/api/SolarCloudApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MutationConfig } from "@/lib/react-query";
import getUserQueryOptions from "../user/useUser";
import { Mutex } from "async-mutex";
import { z } from "zod";
import getNewOrderOptions from "../order/useNewOrder";

export type CreditCardData = {
  number?: string;
  name?: string;
  expiry?: string;
  cvc?: string;
  valid: boolean;
};

export const creditCardSchema = z.object({
  number: z
    .string()
    .transform((val) => val.replace(/\s+/g, "")) // ⬅️ strip all spaces
    .refine((val) => /^\d{15,16}$/.test(val), {
      message: "Card number must be 15 or 16 digits",
    }),
  name: z.string().min(1, { message: "Please enter your name" }),
  // expiry: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Must be in MM/YY"),
  expiry: z
    .string()
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Must be in MM/YY.")
    .refine(
      (value) => {
        const [monthStr, yearStr] = value.split("/");
        const month = parseInt(monthStr, 10);
        const year = parseInt(`20${yearStr}`, 10); // convert YY to YYYY

        if (isNaN(month) || isNaN(year)) return false;

        const now = new Date();
        const expiryDate = new Date(year, month); // the first day of the next month
        return expiryDate > now;
      },
      {
        message: "Invalid Date",
      }
    ),
  cvc: z.string().regex(/^\d{3,4}$/, "Invalid CVC"),
});

const newOrderMutext = new Mutex();

export enum PaymentType {
  Off,
  Apple,
  Google,
  Mint,
  PayPal,
  PayPalPayIn4,
  Contact,
  Stripe,
  PayTo,
  Klarna,
  Afterpay,
}

export type OrderPayment = {
  order: Order;
  payment: {
    type: PaymentType;
    details?: CreditCardData;
  };
};

async function createMintPayment(orderPayment: OrderPayment) {
  const { order, payment } = orderPayment;
  const { details: cardData } = payment!;

  if (
    !cardData?.number ||
    !cardData.expiry ||
    !cardData.cvc ||
    !cardData.name
  ) {
    throw new Error("Error making Mint Payment: Invalid card data");
  }

  const mintCardData: MintCard = {
    number: cardData.number,
    expiryMonth: cardData.expiry!.substring(0, 2),
    expiryYear: cardData.expiry!.substring(2, 4),
    cvc: cardData.cvc!,
    holderName: cardData.name,
  };

  const updatedOrder = await newOrderMutext.runExclusive(
    async () =>
      await DefaultService.newOrderMintPayment(order.uuid!, mintCardData)
  );

  return updatedOrder;
}

type UseCreatePaymentOptions = {
  mutationConfig?: MutationConfig<typeof createMintPayment>;
};

export function useCreatePayment({
  mutationConfig,
}: UseCreatePaymentOptions = {}) {
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, ...args) => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getNewOrderOptions(data.uuid!).queryKey.slice(0, -1),
      });

      onSuccess?.(data, ...args);
    },
    onError: (error, ...args) => {
      console.error("Error creating payment:", error);
      onError?.(error, ...args);
    },
    ...restConfig,
    mutationFn: createMintPayment,
  });
}
